import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';

import { BuilderSelect, BuilderCASelect, BuilderInput } from 'src/features/coverageBuilder';
import { LIFE_AGES } from 'src/features/coverageBuilder/cb_constants';

import {
  compMethodOptions,
  contributionTypeOptions,
  deathBenefitOptions,
  rateBasisOptions,
} from 'src/constants/coverage';
import { currencyFormatter } from 'src/constants/currency';

import UniTobaccoRates from 'src/files/life/uni-tobacco-rates-example.xlsx';
import TobaccoRates from 'src/files/life/tobacco-rates-example.xlsx';

export const useLifeBuild = () => {
  const initialState = {
    id: uuid(),
    ...LIFE_AGES,
    is_current_rate: false,
    is_uni_tobacco: true,
    rate_basis: 'earnings',
    contribution_type: null,
    death_benefit: null,
    rate_per_unit: 1000,
    comp_method: null,
    comp_amount_pct: '10',
    comp_pepm_dollar: null,
    comp_pppm_dollar: null,
    custom_fields: [],
    rate_tier_type: '4tier',
    rate_ee_uni_tobacco: null,
    rate_sp_uni_tobacco: null,
    rate_ch_uni_tobacco: null,
    rate_fam_uni_tobacco: null,
    rate_ee_tobacco: null,
    rate_sp_tobacco: null,
    rate_ch_tobacco: null,
    rate_fam_tobacco: null,
    rate_ee_non_tobacco: null,
    rate_sp_non_tobacco: null,
    rate_ch_non_tobacco: null,
    rate_fam_non_tobacco: null,
  };

  const [formData, setFormData] = useState(initialState);

  const [isDisabledDeathBenefit, setIsDisabledDeathBenefit] = useState(false);

  useEffect(() => {
    if (formData.is_current_rate && formData.contribution_type === 'voluntary') {
      setIsDisabledDeathBenefit(true);
    } else {
      setIsDisabledDeathBenefit(false);
    }
  }, [formData.contribution_type, formData.is_current_rate]);

  const onChange = useCallback(
    (key) => (event) => {
      if (key === 'contribution_type') {
        if (formData.is_current_rate && event.target.value === 'voluntary') {
          return setFormData((prev) => ({
            ...prev,
            [key]: event.target.value,
            rate_basis: 'benefit',
            death_benefit: '0',
          }));
        }

        if (formData.is_current_rate && event.target.value === 'employer') {
          return setFormData((prev) => ({
            ...prev,
            [key]: event.target.value,
            death_benefit: null,
            rate_sp_tobacco: '0',
            rate_sp_non_tobacco: '0',
            rate_sp_uni_tobacco: '0',
            rate_ch_tobacco: '0',
            rate_ch_non_tobacco: '0',
            rate_ch_uni_tobacco: '0',
          }));
        }
      }

      if (key === 'death_benefit') {
        if (event.target.value.includes('salary')) {
          return setFormData((prev) => ({
            ...prev,
            [key]: event.target.value,
            rate_basis: 'earnings',
          }));
        }

        return setFormData((prev) => ({
          ...prev,
          [key]: event.target.value,
          rate_basis: 'benefit',
        }));
      }

      return setFormData((prev) => ({ ...prev, [key]: event.target.value }));
    },
    [formData.is_current_rate],
  );

  const formattedValue = (value) => {
    return currencyFormatter(value, '0');
  };

  const normalizeDeathBenefit = useCallback((value) => {
    if (value === '0') {
      return 'N/A';
    }

    if (_.isNumber(Number(value)) && !isNaN(Number(value))) {
      return formattedValue(value);
    }

    return value;
  }, []);

  const build = {
    coverage: 'life',
    title: 'Life',
    card_options: {
      proposal: [
        {
          title: 'Contribution',
          key: 'contribution_type',
          format: (value) => (value === 'employer' ? 'Employer' : 'Voluntary'),
        },
        {
          title: 'Death Benefit',
          key: 'death_benefit',
          format: (value) => normalizeDeathBenefit(value),
        },
        {
          title: 'Rate Basis',
          key: 'rate_basis',
          format: (value) => value,
        },
        {
          title: 'Current Rate',
          key: 'is_current_rate',
          format: (value) => (value ? 'Yes' : 'No'),
        },
      ],
    },
    form_options: {
      initial_state: initialState,
      import_data: [
        {
          title: 'Download sample template for Uni-tobacco age rates',
          file: UniTobaccoRates,
          file_name: 'uni-tobacco-rates-example',
          key: 'uni_tobacco',
        },
        {
          title: 'Download sample template for Tobacco/Non-Tobacco age rates',
          file: TobaccoRates,
          file_name: 'tobacco-rates-example',
          key: 'tobacco',
        },
      ],
      form_data: formData,
      setFormData,
    },
    form_content: () => [
      <BuilderSelect
        key="1"
        label="Employer Contribution"
        value={formData.contribution_type}
        options={contributionTypeOptions}
        onChange={onChange('contribution_type')}
      />,
      ...(!isDisabledDeathBenefit
        ? [
            <BuilderSelect
              key="2"
              label="Death Benefit"
              value={formData.death_benefit}
              options={deathBenefitOptions}
              onChange={onChange('death_benefit')}
              type="other"
              symbol="$"
              inputType="number"
              disabled={isDisabledDeathBenefit}
            />,
          ]
        : []),
      <BuilderInput
        key="3"
        label="Rate Per Unit"
        value={formData.rate_per_unit}
        onChange={onChange('rate_per_unit')}
        symbol="$"
        inputType="number"
      />,
      <BuilderSelect
        key="4"
        label="Rate Basis"
        value={formData.rate_basis}
        options={rateBasisOptions}
        onChange={onChange('rate_basis')}
        disabled={true}
      />,
      <BuilderSelect
        key="5"
        label="Compensation Method"
        value={formData.comp_method}
        options={compMethodOptions}
        onChange={onChange('comp_method')}
      />,
      <BuilderCASelect
        key="6"
        size="small"
        label="Compensation Amount"
        formData={formData}
        setFormData={setFormData}
      />,
    ],
    offer_details: [
      { key: 'contribution_type' },
      {
        key: 'death_benefit',
        format: (value) => normalizeDeathBenefit(value),
      },
      { key: 'rate_per_unit', format: (value) => formattedValue(value) },
      { key: 'rate_basis' },
      { key: 'comp_method' },
    ],
  };

  return { build };
};
